import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import type { OsvComponent } from '@kirin/api-client/contracts/shared/osv';
import {
  PLATFORM_REGISTRY,
  toPlatformId,
} from '@kirin/api-client/contracts/platform-registry';
import { hasKnownEntityName } from '@kirin/api-client/helpers/source-details';
import { getUserDisplayName } from '@/lib/utils';
import { hasSecretDetails } from './AlertSecretDetails.helpers';
import {
  ORGANIZATION_SCOPE_LABEL,
  securityGroupLabel,
} from './security-groups';

/**
 * KIR-4481 — presentation logic for the grouped-cards Alert Details panel.
 *
 * Kept as pure functions (no JSX) so the card-presence and representative-value
 * rules can be unit-tested directly. Rendering lives in the sibling components.
 */

/**
 * The card categories, each with its own accent colour (per the mockup).
 */
export type AlertCardCategory =
  | 'what-this-means'
  | 'location'
  | 'detected-on-client'
  | 'server-json'
  // KIR-4271: guidance, not a finding — hence its own colour rather than
  // reusing the amber "what this means" accent it sits below.
  | 'recommended-actions'
  // KIR-4836: the tool-call-scan pair, which replaces Location +
  // Detected-on-client for that type only.
  | 'tool-call'
  | 'why-flagged';

/** Tailwind classes per category: left-accent border, title text, title bullet. */
export const CARD_ACCENT_CLASSES: Record<
  AlertCardCategory,
  { border: string; title: string; bullet: string }
> = {
  'what-this-means': {
    border: 'border-l-amber-500',
    title: 'text-amber-600 dark:text-amber-400',
    bullet: 'bg-amber-500',
  },
  location: {
    border: 'border-l-blue-500',
    title: 'text-blue-600 dark:text-blue-400',
    bullet: 'bg-blue-500',
  },
  'detected-on-client': {
    border: 'border-l-violet-500',
    title: 'text-violet-600 dark:text-violet-400',
    bullet: 'bg-violet-500',
  },
  'server-json': {
    border: 'border-l-slate-500',
    title: 'text-slate-600 dark:text-slate-300',
    bullet: 'bg-slate-500',
  },
  'recommended-actions': {
    border: 'border-l-emerald-500',
    title: 'text-emerald-600 dark:text-emerald-400',
    bullet: 'bg-emerald-500',
  },
  'tool-call': {
    border: 'border-l-rose-500',
    title: 'text-rose-600 dark:text-rose-400',
    bullet: 'bg-rose-500',
  },
  'why-flagged': {
    border: 'border-l-violet-500',
    title: 'text-violet-600 dark:text-violet-400',
    bullet: 'bg-violet-500',
  },
};

/** Fill colour of the risk-score bar, banded so a low score reads as low-risk. */
export function riskScoreBarClass(score: number): string {
  if (score >= 70) return 'bg-red-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-emerald-500';
}

type SourceDetails = GroupedAlert['sourceDetails'];

/** The MCP server-definition JSON block, when this alert carries one. */
export function getConfigJsonBlock(
  sourceDetails: SourceDetails
): string | undefined {
  return sourceDetails?.kind === 'mcp'
    ? (sourceDetails.configJsonBlock ?? undefined)
    : undefined;
}

/** OSV component behind a vulnerable-dependency finding (extension or MCP). */
export function getOsvComponent(
  sourceDetails: SourceDetails
): OsvComponent | undefined {
  if (sourceDetails?.kind === 'extension' || sourceDetails?.kind === 'mcp') {
    return sourceDetails.osvComponent;
  }
  return undefined;
}

/** The OSV advisory id (CVE/GHSA/MAL), when present on the source details. */
export function getOsvAdvisoryId(
  sourceDetails: SourceDetails
): string | undefined {
  if (
    sourceDetails?.kind === 'extension' ||
    sourceDetails?.kind === 'mcp' ||
    sourceDetails?.kind === 'package'
  ) {
    return sourceDetails.osvId;
  }
  return undefined;
}

/**
 * The "Location" card shows when there is an OSV component (its Package /
 * Ecosystem / Version rows) or any location/file string to display.
 */
export function hasLocationCard(alert: {
  location?: string | null;
  sourceDetails?: SourceDetails;
  sourceDetailsEnabled: boolean;
}): boolean {
  const osv = alert.sourceDetailsEnabled
    ? getOsvComponent(alert.sourceDetails)
    : undefined;
  return Boolean(osv) || Boolean(alert.location);
}

/**
 * The "Detected on client" card shows when there is source-detail (extension /
 * MCP entity or PII sample) or secret-detail content to render. Detection Logic
 * always renders inside this card when its flag is on, so an alert with only a
 * detection reason still gets the card.
 */
export function hasDetectedOnClientCard(alert: {
  sourceDetails?: SourceDetails;
  sourceDetailsEnabled: boolean;
  secretDetails: Parameters<typeof hasSecretDetails>[0];
  secretDetailsEnabled: boolean;
  detectionLogicEnabled: boolean;
}): boolean {
  if (alert.detectionLogicEnabled) return true;
  const hasSource =
    alert.sourceDetails?.kind === 'pii' ||
    (alert.sourceDetailsEnabled &&
      alert.sourceDetails != null &&
      hasKnownEntityName(alert.sourceDetails));
  const hasSecret =
    alert.secretDetailsEnabled && hasSecretDetails(alert.secretDetails);
  return Boolean(hasSource) || hasSecret;
}

/**
 * The "Server definition (JSON)" card shows only for MCP alerts with a config,
 * and only when the entity name is known — mirroring the legacy card, which
 * hosted the JSON inside `AlertSourceDetails` and so gated it on
 * `hasKnownEntityName`. An MCP finding with no parseable config (Unknown name)
 * showed no JSON there and must not here either.
 */
export function hasServerJsonCard(alert: {
  sourceDetails?: SourceDetails;
  sourceDetailsEnabled: boolean;
}): boolean {
  return (
    alert.sourceDetailsEnabled &&
    alert.sourceDetails != null &&
    hasKnownEntityName(alert.sourceDetails) &&
    getConfigJsonBlock(alert.sourceDetails) != null
  );
}

/** Facts-strip "USER" cell: representative user name, with a +N overflow. */
export function representativeUserLabel(
  users: GroupedAlert['users']
): string | null {
  if (!users || users.length === 0) return null;
  const first = getUserDisplayName(users[0]);
  return users.length > 1 ? `${first} +${users.length - 1}` : first;
}

/** Facts-strip "ENDPOINT" cell: representative endpoint name, with a +N overflow. */
export function representativeEndpointLabel(
  endpoints: GroupedAlert['endpoints']
): string | null {
  if (!endpoints || endpoints.length === 0) return null;
  const first = endpoints[0].name || endpoints[0].machineId;
  return endpoints.length > 1 ? `${first} +${endpoints.length - 1}` : first;
}

/**
 * Facts-strip "SECURITY GROUP" cell: representative group, or the org scope. A
 * multi-group alert appends a "+N" overflow (like the user/endpoint cells) so it
 * doesn't silently hide the other groups; Show Instances breaks them down.
 */
export function representativeSecurityGroupLabel(
  securityGroups: GroupedAlert['securityGroups']
): string | null {
  if (securityGroups === undefined) return null;
  if (securityGroups.length === 0) return ORGANIZATION_SCOPE_LABEL;
  const first = securityGroupLabel(securityGroups[0]);
  return securityGroups.length > 1
    ? `${first} +${securityGroups.length - 1}`
    : first;
}

/** Facts-strip "IDE / PLATFORM" cell: client types mapped to display names. */
export function platformLabel(
  clientTypes: GroupedAlert['clientTypes']
): string | null {
  if (!clientTypes || clientTypes.length === 0) return null;
  // copilot and github_copilot_cli share a display name. Print it once.
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const clientType of clientTypes) {
    const id = toPlatformId(clientType);
    const label = id ? PLATFORM_REGISTRY[id].displayName : clientType;
    if (seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels.join(', ');
}
